import React from "react";
import { useTranslation } from "react-i18next";
import { AdminUser } from "../../features/admin/models/AdminUser";
import AdminUserRow from "./AdminUserRow";

interface AdminUsersListProps {
  users: AdminUser[];
  isLoading: boolean;
  isFailed: boolean;
  totalCount: number;
  totalPages: number;
  page: number;
  onPageChange: (page: number) => void;
  onManageComplimentary: (user: AdminUser) => void;
  onChangeLoginEmail: (user: AdminUser) => void;
  onManageCustomPrice: (user: AdminUser) => void;
}

/** The admin user overview list with its loading/empty/error states and pager. */
const AdminUsersList: React.FC<AdminUsersListProps> = ({
  users,
  isLoading,
  isFailed,
  totalCount,
  totalPages,
  page,
  onPageChange,
  onManageComplimentary,
  onChangeLoginEmail,
  onManageCustomPrice,
}) => {
  const { t } = useTranslation();

  if (isFailed) {
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
        <p>{t("admin.errorLoading")}</p>
      </div>
    );
  }

  if (isLoading && users.length === 0) {
    return (
      <p className="py-10 text-center text-grey-40 dark:text-grey-70">{t("admin.loading")}</p>
    );
  }

  if (users.length === 0) {
    return (
      <div className="with-border rounded-2xl p-8 text-center text-grey-40 dark:text-grey-70">
        {t("admin.empty")}
      </div>
    );
  }

  return (
    <>
      <p className="text-sm text-grey-50 dark:text-grey-60">
        {t("admin.totalUsers", { count: totalCount })}
      </p>
      <div className="grid grid-cols-1 gap-2 lg:gap-4 2xl:grid-cols-2">
        {users.map((user) => (
          <AdminUserRow
            key={user.instructorId}
            user={user}
            onManageComplimentary={onManageComplimentary}
            onChangeLoginEmail={onChangeLoginEmail}
            onManageCustomPrice={onManageCustomPrice}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            className="btn btn-secondary"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          >
            {t("admin.previous")}
          </button>
          <span className="text-sm text-grey-50 dark:text-grey-60">
            {t("admin.pageOf", { page, totalPages })}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          >
            {t("admin.next")}
          </button>
        </div>
      )}
    </>
  );
};

export default AdminUsersList;
